import { CALLOUT_NODE } from "@repo/fika-blog/src/nodes/callout-node"
import type { Editor } from "@repo/fika-blog/src/tiptap/core"
import type { Emoji } from "solid-emoji-picker"
import { createEffect, createSignal, onCleanup, Show } from "solid-js"
import { EmojiPicker } from "@/designSystem/components/emoji-picker"
import { calloutEmojiPopupClass } from "./callout-emoji-menu.css"

interface CalloutEmojiMenuProps {
  editor?: Editor
}

export function CalloutEmojiMenu(props: CalloutEmojiMenuProps) {
  let popupRef: HTMLDivElement | undefined

  const [isOpen, setIsOpen] = createSignal(false)
  const [position, setPosition] = createSignal({ top: 0, left: 0 })
  const [calloutFrom, setCalloutFrom] = createSignal<number | null>(null)

  function resolveCalloutFrom(emojiTrigger: Element): number | null {
    const editor = props.editor
    if (!editor) return null

    let pos: number | null = null
    try {
      pos = editor.view.posAtDOM(emojiTrigger, 0)
    } catch {
      return null
    }
    if (pos === null) return null

    const clampedPos = Math.max(0, Math.min(pos, editor.state.doc.content.size))
    const $pos = editor.state.doc.resolve(clampedPos)

    if ($pos.depth === 0) {
      const node = $pos.nodeAfter
      if (!node || node.type.name !== CALLOUT_NODE) return null
      return clampedPos
    }

    const node = $pos.node(1)
    if (!node || node.type.name !== CALLOUT_NODE) return null
    return $pos.before(1)
  }

  function updateEmoji(emoji: string) {
    const editor = props.editor
    const from = calloutFrom()
    if (!editor || from === null) return

    const node = editor.state.doc.nodeAt(from)
    if (!node || node.type.name !== CALLOUT_NODE) return

    const to = from + node.nodeSize
    const textPos = Math.min(from + 1, to - 1)

    editor
      .chain()
      .focus()
      .setTextSelection(textPos)
      .updateAttributes(CALLOUT_NODE, { emoji })
      .run()
  }

  function onEmojiClick(emoji: Emoji) {
    updateEmoji(emoji.emoji)
    setIsOpen(false)
  }

  function onEditorClick(event: MouseEvent) {
    const target =
      event.target instanceof HTMLElement ? event.target : undefined
    if (!target) return

    const emojiTrigger = target.closest("[data-callout-emoji-trigger='true']")
    if (!emojiTrigger) return

    const from = resolveCalloutFrom(emojiTrigger)
    if (from === null) return

    event.preventDefault()
    event.stopPropagation()

    if (isOpen() && calloutFrom() === from) {
      setIsOpen(false)
      return
    }

    const rect = emojiTrigger.getBoundingClientRect()
    setPosition({ top: rect.bottom + 4, left: rect.left })
    setCalloutFrom(from)
    setIsOpen(true)
  }

  function onDocumentMouseDown(event: MouseEvent) {
    if (!isOpen()) return
    const target = event.target
    if (!(target instanceof HTMLElement)) return
    if (popupRef?.contains(target)) return
    if (target.closest("[data-callout-emoji-trigger='true']")) return
    setIsOpen(false)
  }

  function onKeyDown(event: KeyboardEvent) {
    if (event.key === "Escape" && isOpen()) {
      setIsOpen(false)
    }
  }

  createEffect(() => {
    const editor = props.editor
    if (!editor) return

    const editorDom = editor.view.dom
    editorDom.addEventListener("click", onEditorClick)
    document.addEventListener("mousedown", onDocumentMouseDown)
    document.addEventListener("keydown", onKeyDown)

    onCleanup(() => {
      editorDom.removeEventListener("click", onEditorClick)
      document.removeEventListener("mousedown", onDocumentMouseDown)
      document.removeEventListener("keydown", onKeyDown)
    })
  })

  return (
    <Show when={isOpen()}>
      <div
        ref={popupRef}
        class={calloutEmojiPopupClass}
        style={{
          top: `${position().top}px`,
          left: `${position().left}px`,
        }}
      >
        <EmojiPicker onEmojiClick={onEmojiClick} />
      </div>
    </Show>
  )
}
